import type { PluginConnection, PluginToolkit } from "@ark-bot/protocol";

/**
 * What the Plugins panel shows for each app (ARCHITECTURE §6.18), worked out
 * from the catalogue and the user's connections, apart from the component
 * so it can be tested without a page.
 */

/** A sign-in the user started and walked away from stops counting as one after this. */
export const PENDING_STALE_MS = 10 * 60_000;

export type PluginRowState =
  | { readonly kind: "connect" }
  | { readonly kind: "no-auth" }
  | { readonly kind: "active"; readonly connection: PluginConnection }
  | { readonly kind: "pending"; readonly connection: PluginConnection }
  | { readonly kind: "broken"; readonly connection: PluginConnection };

const RANK: Record<PluginConnection["state"], number> = { active: 0, pending: 1, broken: 2 };

function createdAt(connection: PluginConnection): number {
  const at = Date.parse(connection.createdAt);
  return Number.isFinite(at) ? at : 0;
}

/** Whether a pending sign-in is still worth waiting on. */
export function livePending(connection: PluginConnection, now: number): boolean {
  return connection.state === "pending" && now - createdAt(connection) < PENDING_STALE_MS;
}

/**
 * Each app's connection that decides its row: a working one over one being
 * signed in over a broken one, and the newest of those. A stale pending
 * sign-in decides nothing.
 */
export function connectionsByToolkit(connections: readonly PluginConnection[], now: number): Map<string, PluginConnection> {
  const best = new Map<string, PluginConnection>();
  for (const connection of connections) {
    if (connection.state === "pending" && !livePending(connection, now)) continue;
    const held = best.get(connection.toolkit);
    if (held == null || RANK[connection.state] < RANK[held.state] || (RANK[connection.state] === RANK[held.state] && createdAt(connection) > createdAt(held))) best.set(connection.toolkit, connection);
  }
  return best;
}

export function rowState(toolkit: PluginToolkit, byToolkit: ReadonlyMap<string, PluginConnection>): PluginRowState {
  const connection = byToolkit.get(toolkit.slug);
  if (connection != null) return { kind: connection.state, connection };
  return toolkit.noAuth ? { kind: "no-auth" } : { kind: "connect" };
}

/** The apps the user has connected, or is connecting, first; otherwise the catalogue's own order (most used, or best match). */
export function orderToolkits(items: readonly PluginToolkit[], byToolkit: ReadonlyMap<string, PluginConnection>): PluginToolkit[] {
  const rank = (toolkit: PluginToolkit): number => {
    const connection = byToolkit.get(toolkit.slug);
    return connection == null ? 3 : RANK[connection.state];
  };
  return items
    .map((toolkit, index) => ({ toolkit, index }))
    .sort((a, b) => rank(a.toolkit) - rank(b.toolkit) || a.index - b.index)
    .map(({ toolkit }) => toolkit);
}

/** A page appended to what is shown, without showing an app twice when the catalogue shifts underneath. */
export function appendPage(shown: readonly PluginToolkit[], page: readonly PluginToolkit[]): PluginToolkit[] {
  const seen = new Set(shown.map((toolkit) => toolkit.slug));
  return [...shown, ...page.filter((toolkit) => !seen.has(toolkit.slug))];
}

/**
 * The Connected tab's rows: every app with a connection, drawn from what the
 * catalogue has shown so far, or a plain stand-in for one it has not.
 */
export function connectedToolkits(byToolkit: ReadonlyMap<string, PluginConnection>, known: ReadonlyMap<string, PluginToolkit>): PluginToolkit[] {
  const rows = [...byToolkit.keys()].map(
    (slug): PluginToolkit => known.get(slug) ?? { slug, name: slug.charAt(0).toUpperCase() + slug.slice(1), description: "", logo: "", categories: [], toolsCount: 0, noAuth: false },
  );
  return orderToolkits(rows, byToolkit);
}

/** Whether anything is mid sign-in, so the panel keeps asking until it lands. */
export function anyPending(connections: readonly PluginConnection[], now: number): boolean {
  return connections.some((connection) => livePending(connection, now));
}
